// @i18n-ignore-file

import { useStatsigClient } from '@statsig/react-bindings'

import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { withShopPathVariants } from '@src/server-side/server-dynamic-config/excluded-sitemap-urls.constants'

const SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG_KEY = 'clp_show_second_image_on_product_tiles'

export interface ShowSecondImageOnProductTilesConfig {
  enabled?: boolean
  enabledForSlugs?: string[]
}

/**
 * Default config used when Statsig is unavailable / the config value is empty.
 * Mirrors the slug list previously hard-coded in the codebase before this was moved to Statsig.
 */
export const DEFAULT_SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG: ShowSecondImageOnProductTilesConfig = {
  enabled: true,
  // [STRFRNT-11724] withShopPathVariants adds /shop/* twins so UK region 103 matches too.
  enabledForSlugs: withShopPathVariants([
    '/home/duvet-covers-and-shams/lifestyle',
    '/home/quilts-and-coverlets',
    '/home/duvet-covers-and-shams',
    '/baby-girl/sleep-bags/lifestyle',
    '/baby-boy/sleep-bags/lifestyle',
    '/toddler-girl/sleep-bags/lifestyle',
    '/toddler-boy/sleep-bags/lifestyle',
    '/home/pillows-&-decor/lifestyle',
    '/home/throws-&-blankets/lifestyle',
  ]),
}

export const useStatsigShowSecondImageOnProductTilesConfig = (): ShowSecondImageOnProductTilesConfig => {
  const { getDynamicConfig } = useStatsigClient()

  try {
    const config = getDynamicConfig(SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG_KEY)
    const configValue = config.value as unknown as ShowSecondImageOnProductTilesConfig | null | undefined

    return {
      enabled: configValue?.enabled ?? DEFAULT_SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG.enabled,
      enabledForSlugs:
        configValue?.enabledForSlugs != null
          ? withShopPathVariants(configValue.enabledForSlugs)
          : DEFAULT_SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG.enabledForSlugs,
    }
  } catch (error) {
    captureException(error as Error, {
      module: MODULES.COLLECTION,
      feature: FEATURES.CLP_IMAGE_CONFIG,
      additionalTags: { source: 'useStatsigShowSecondImageOnProductTilesConfig' },
      extraInfo: { configKey: SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG_KEY },
    })
    return DEFAULT_SHOW_SECOND_IMAGE_ON_PRODUCT_TILES_CONFIG
  }
}

export const isShowSecondImageEnabledForSlug = (config: ShowSecondImageOnProductTilesConfig, slug: string): boolean => {
  if (!config.enabled) return false
  return config.enabledForSlugs?.includes(slug) ?? false
}
